import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Image,
  Switch,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Vehicle } from '../../types';
import { useVehicleStore } from '../../stores/vehicleStore';
import theme from '../../theme';

export const VehicleDetailsScreen = ({ navigation, route }: any) => {
  const { vehicleId } = route.params;
  const { vehicles, editVehicle, loading } = useVehicleStore();
  const [isEditing, setIsEditing] = useState(false);
  const [vehicleData, setVehicleData] = useState<Vehicle | null>(null);

  useEffect(() => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setVehicleData(vehicle);
    }
  }, [vehicleId, vehicles]);

  const handleInputChange = (field: keyof Vehicle, value: string | number) => {
    if (vehicleData) {
      setVehicleData(prev => ({
        ...prev!,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!vehicleData) return;

    try {
      console.log('💾 Saving vehicle:', vehicleData.id);
      console.log('📝 Vehicle data being saved:', {
        licensePlate: vehicleData.licensePlate,
        makeModel: vehicleData.makeModel,
        year: vehicleData.year,
        vinNumber: vehicleData.vinNumber,
        carStatus: vehicleData.carStatus
      });
      
      await editVehicle(vehicleData.id, vehicleData);
      
      // Force refresh from store to get latest data
      const { fetchVehicles } = useVehicleStore.getState();
      await fetchVehicles();
      
      // Show success message immediately (platform-specific)
      if (Platform.OS === 'web') {
        alert('نجح / Success\n\nتم تحديث المركبة بنجاح\nVehicle updated successfully');
      } else {
        Alert.alert(
          'نجح / Success', 
          'تم تحديث المركبة بنجاح\nVehicle updated successfully'
        );
      }
      
      // Update UI state
      setIsEditing(false);
      
      // Refresh vehicle data from store
      const updatedVehicle = vehicles.find(v => v.id === vehicleId);
      if (updatedVehicle) {
        setVehicleData(updatedVehicle);
      }
    } catch (error) {
      console.error('❌ Error updating vehicle:', error);
      if (Platform.OS === 'web') {
        alert('خطأ / Error\n\nفشل تحديث المركبة\nFailed to update vehicle. Please try again.');
      } else {
        Alert.alert('خطأ / Error', 'فشل تحديث المركبة\nFailed to update vehicle. Please try again.');
      }
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (vehicleData) {
      setVehicleData(prev => ({
        ...prev!,
        carStatus: newStatus as Vehicle['carStatus']
      }));
    }
  };

  if (!vehicleData) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.title}>Vehicle Not Found</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>
          {vehicleData.licensePlate} - {vehicleData.makeModel}
        </Text>
        <TouchableOpacity
          onPress={() => setIsEditing(!isEditing)}
          style={styles.editButton}
        >
          <Ionicons 
            name={isEditing ? "close" : "pencil"} 
            size={20} 
            color={theme.colors.primary} 
          />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        {/* Vehicle Image */}
        {vehicleData.image_url && (
          <Card style={styles.imageCard}>
            <Image source={{ uri: vehicleData.image_url }} style={styles.vehicleImage} />
          </Card>
        )}

        {/* Basic Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Basic Information</Text>
          
          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>License Plate</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={vehicleData.licensePlate}
                onChangeText={(value) => handleInputChange('licensePlate', value)}
                editable={isEditing}
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Vehicle Code</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={vehicleData.vehicleCode || ''}
                onChangeText={(value) => handleInputChange('vehicleCode', value)}
                editable={isEditing}
              />
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Make & Model</Text>
            <TextInput
              style={[styles.input, !isEditing && styles.inputDisabled]}
              value={vehicleData.makeModel}
              onChangeText={(value) => handleInputChange('makeModel', value)}
              editable={isEditing}
            />
          </View>

          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>Year</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={String(vehicleData.year || '')}
                onChangeText={(value) => handleInputChange('year', value)}
                editable={isEditing}
                keyboardType="numeric"
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>VIN Number</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={vehicleData.vin || vehicleData.vinNumber || ''}
                onChangeText={(value) => handleInputChange('vin', value)}
                editable={isEditing}
              />
            </View>
          </View>
        </Card>

        {/* Status & Assignment */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Status & Assignment</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Current Status</Text>
            {isEditing ? (
              <View style={styles.statusButtons}>
                {['available', 'work', 'in_workshop', 'out_of_service', 'stop'].map((status) => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.statusButton,
                      vehicleData.carStatus === status && styles.statusButtonActive
                    ]}
                    onPress={() => handleStatusChange(status)}
                  >
                    <Text style={[
                      styles.statusButtonText,
                      vehicleData.carStatus === status && styles.statusButtonTextActive
                    ]}>
                      {status.replace('_', ' ').toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <View style={[styles.statusDisplay, { backgroundColor: getStatusColor(vehicleData.carStatus) }]}>
                <Text style={styles.statusDisplayText}>
                  {(vehicleData.carStatus || 'unknown').replace('_', ' ').toUpperCase()}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Assigned Driver</Text>
            <TextInput
              style={[styles.input, !isEditing && styles.inputDisabled]}
              value={vehicleData.currentDriverId || vehicleData.assignedDriverId || 'Not Assigned'}
              onChangeText={(value) => handleInputChange('currentDriverId', value)}
              editable={isEditing}
              placeholder="Driver ID or Name"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Daily Company Share (AED)</Text>
            <TextInput
              style={[styles.input, !isEditing && styles.inputDisabled]}
              value={String(vehicleData.dailyRent || vehicleData.dailyRate || vehicleData.dailyRentalRate || '')}
              onChangeText={(value) => handleInputChange('dailyRent', parseFloat(value) || 0)}
              editable={isEditing}
              keyboardType="numeric"
              placeholder="0"
            />
          </View>
        </Card>

        {/* Technical Details */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Technical Details</Text>
          
          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>Current Mileage</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={String(vehicleData.currentMileage || vehicleData.odometerReading || '')}
                onChangeText={(value) => handleInputChange('currentMileage', parseFloat(value) || 0)}
                editable={isEditing}
                keyboardType="numeric"
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Fleet Type</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={vehicleData.fleetType || 'FLEET'}
                onChangeText={(value) => handleInputChange('fleetType', value)}
                editable={isEditing}
              />
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea, !isEditing && styles.inputDisabled]}
              value={vehicleData.notes || ''}
              onChangeText={(value) => handleInputChange('notes', value)}
              editable={isEditing}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>
        </Card>

        {/* Action Buttons */}
        {isEditing && (
          <View style={styles.actionButtons}>
            <Button
              title={loading ? "Saving..." : "Save Changes"}
              onPress={handleSave}
              disabled={loading}
              variant="primary"
              style={styles.saveButton}
            />
            <Button
              title="Cancel"
              onPress={() => {
                setIsEditing(false);
                // Reset to original data
                const originalVehicle = vehicles.find(v => v.id === vehicleId);
                if (originalVehicle) {
                  setVehicleData(originalVehicle);
                }
              }}
              variant="secondary"
              style={styles.cancelButton}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case 'available':
      return theme.colors.success;
    case 'work':
      return theme.colors.warning;
    case 'in_workshop':
      return theme.colors.info;
    case 'out_of_service':
    case 'stop':
      return theme.colors.error;
    default:
      return theme.colors.gray400;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    flex: 1,
    marginLeft: theme.spacing.md,
  },
  editButton: {
    padding: theme.spacing.sm,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  imageCard: {
    marginBottom: theme.spacing.lg,
    padding: 0,
    overflow: 'hidden',
  },
  vehicleImage: {
    width: '100%',
    height: 200,
    resizeMode: 'cover',
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  row: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  field: {
    flex: 1,
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  inputDisabled: {
    backgroundColor: theme.colors.gray50,
    color: theme.colors.textSecondary,
  },
  textArea: {
    height: 80,
    paddingTop: theme.spacing.sm,
  },
  statusButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  statusButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.round,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    backgroundColor: theme.colors.white,
  },
  statusButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  statusButtonText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
  statusButtonTextActive: {
    color: theme.colors.white,
  },
  statusDisplay: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  statusDisplayText: {
    color: theme.colors.white,
    fontWeight: '500' as any,
    fontSize: theme.typography.sizes.sm,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginVertical: theme.spacing.lg,
  },
  saveButton: {
    flex: 1,
  },
  cancelButton: {
    flex: 1,
  },
});

export default VehicleDetailsScreen;
